import { kebabCase } from 'change-case';

import type {
  CSSVariable,
  RootCSSVariable,
  Scheme,
} from '@personly/libs-types';

type ThemeColor = {
  media: string;
  color: string;
};

const toRootCssVariables = (cssVariables: CSSVariable[]) =>
  cssVariables.reduce<RootCSSVariable[]>((prev, { key, value }) => {
    if (value.length > 1) {
      const expandedValues = value.reduce<RootCSSVariable[]>(
        (a, b, i) => [...a, { key: `${kebabCase(key)}-${i}`, value: b }],
        []
      );

      prev.push(...expandedValues);
    } else prev.push({ key: kebabCase(key), value: value[0] });

    return prev;
  }, []);

const toPreferSchemeCss = (
  rootCss: string,
  scheme: string
) => `@media (prefers-color-scheme: ${scheme}) {
${rootCss}
}`;

const toRootCss = (cssVariables: CSSVariable[], numIndent = 0) => {
  const rootCssVariables = toRootCssVariables(cssVariables);
  const indent = ' ';

  const outerIndent = indent.repeat(numIndent);
  const innerIndent = indent.repeat(numIndent + 2);

  return `${outerIndent}:root {
${rootCssVariables
  .reduce<
    string[]
  >((prev, { key, value }) => [...prev, `${innerIndent}--${key}: ${value};`], [])
  .join(`\n`)}
${outerIndent}}`;
};

const toTailwindCssVariables = (cssVariables: CSSVariable[]) =>
  cssVariables
    .reduce<{ key: string; value: string }[]>((acc, { key, value }) => {
      if (value.length > 1) {
        acc.push(
          ...value.reduce<{ key: string; value: string }[]>(
            (a, _value, i) => [
              ...a,
              {
                key: `${kebabCase(key)}${i}`,
                value: `var(--${kebabCase(key)}-${i})`,
              },
            ],
            []
          )
        );
      } else
        acc.push({
          key: kebabCase(key),
          value: `var(--${kebabCase(key)})`,
        });

      return acc;
    }, [])
    .reduce<Record<string, string>>(
      (acc, cur) => ({ ...acc, [cur.key]: cur.value }),
      {}
    );

const toCss = ({ schemes, scheme }: { schemes: Scheme[]; scheme: string }) => {
  if (scheme === 'SYSTEM') {
    return schemes
      .filter(({ id }) => id !== 'SYSTEM')
      .reduce<string[]>((acc, { id, cssVariables }) => {
        const rootCss = toRootCss(cssVariables, 2);
        const schemeCss = toPreferSchemeCss(rootCss, id.toLowerCase());
        return [...acc, schemeCss];
      }, [])
      .join(`\n\n`);
  }

  const activeScheme = schemes.find(({ id }) => id === scheme);
  if (!activeScheme) return ``;

  return toRootCss(activeScheme.cssVariables);
};

const gatherCssVariables = (schemes: Scheme[]) =>
  schemes.reduce<CSSVariable[]>((cssVariables, scheme) => {
    for (const cssVariable of scheme.cssVariables) {
      const i = cssVariables.findIndex(({ key }) => key === cssVariable.key);
      if (i === -1) cssVariables.push(cssVariable);
    }

    return cssVariables;
  }, []);

const makeMedia = (scheme: Scheme) =>
  `(prefers-color-scheme: ${scheme.id.toLowerCase()})`;

const makeColor = (scheme: Scheme, key: string) => {
  const cssVariable = scheme.cssVariables.find(
    (cssVariable) => key === cssVariable.key
  );

  return cssVariable ? cssVariable.value[0] : null;
};

const toThemeColor = (schemes: Scheme[], key: string) =>
  schemes.reduce<ThemeColor[]>((acc, scheme) => {
    const color = makeColor(scheme, key);
    if (!color) return acc;
    const media = makeMedia(scheme);
    return [...acc, { media, color }];
  }, []);

export {
  gatherCssVariables,
  toCss,
  toRootCssVariables,
  toTailwindCssVariables,
  toThemeColor,
};
